import { toast } from 'react-toastify'; // Assuming you have react-toastify installed and configured
import { SERVICES_API_BASE_URL } from './apiConfig';

const BASE_URL = `${SERVICES_API_BASE_URL}/api`;

// Helper to get authorization headers
const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  if (!token) {
    // In a real app, you might redirect to login or throw an error
    console.error("No authentication token found.");
    return {};
  }
  return {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${token}`
  };
};

// Generic API call wrapper
const apiCall = async (endpoint: string, method: string, data?: any) => {
  try {
    const headers = getAuthHeaders();
    const config: RequestInit = {
      method,
      headers,
    };

    if (data) {
      config.body = JSON.stringify(data);
    }

    const response = await fetch(`${BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || "Something went wrong.");
    } else if (response.status === 401) {
      // Handle unauthorized specifically, e.g., redirect to login
      toast.error("Unauthorized. Please log in again.");
      // You might want to clear token and redirect here
      // localStorage.removeItem("token");
      // window.location.href = "/login";
      throw new Error("Unauthorized");
    }

    // Read as text first to handle empty bodies (like 200 OK or 204 No Content)
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error: any) {
    console.error(`API Error (${method} ${endpoint}):`, error);
    toast.error(error.message || "Something went wrong. Please try again.");
    throw error; // Re-throw to allow component to handle specific errors or loading states
  }
};

// --- API Endpoints ---

export interface InvoiceItem {
  description: string;
  quantity: number;
  unitPrice: number;
}

export interface CreateInvoiceRequest {
  customerName: string;
  customerEmail: string;
  customerAddress: string;
  customerPhone: string;
  paymentTermsDays: number;
  issueDate: string; // ISO 8601 string
  items: InvoiceItem[];
}

export enum BackendInvoiceStatus {
  Draft = 0,
  Sent = 1,
  PartialPaid = 2,
  Paid = 3,
  Overdue = 4,
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  customerName: string;
  customerEmail: string;
  customerAddress?: string;
  customerPhone?: string;
  totalAmount: number;
  paidAmount?: number;
  paymentLink?: string;
  status: BackendInvoiceStatus;
  issueDate: string;
  paymentTermsDays: number;
  dueDate: string; // ISO 8601 string
  items: InvoiceItem[];
}

export const billingApi = {
  /**
   * Creates a new invoice.
   * @param data The invoice data to send.
   * @returns The created invoice object.
   */
  createInvoice: async (data: CreateInvoiceRequest): Promise<Invoice> => {
    return apiCall("/invoice/create", "POST", data);
  },

  /**
   * Fetches all invoices for the current user.
   * @returns An array of invoices.
   */
  getInvoices: async (): Promise<Invoice[]> => {
    return apiCall("/invoice", "GET");
  },

  /**
   * Fetches a single invoice by its ID.
   * @param id The ID of the invoice to fetch.
   * @returns The invoice object.
   */
  getInvoiceById: async (id: string): Promise<Invoice> => {
    return apiCall(`/invoice/${id}`, "GET");
  },

  /**
   * Updates an existing invoice.
   * @param id The ID of the invoice to update.
   * @param data The updated invoice data.
   * @returns The updated invoice object.
   */
  updateInvoice: async (id: string, data: CreateInvoiceRequest): Promise<Invoice> => {
    return apiCall(`/invoice/${id}`, "PUT", data);
  },

  /**
   * Deletes an invoice by its ID.
   * @param id The ID of the invoice to delete.
   */
  deleteInvoice: async (id: string): Promise<void> => {
    return apiCall(`/invoice/${id}`, "DELETE");
  },

  /**
   * Changes an invoice status from Draft to Sent.
   * @param id The ID of the invoice to send.
   */
  sendInvoice: async (id: string): Promise<Invoice> => {
    return apiCall(`/invoice/${id}/send`, "POST");
  },

  downloadPdf: async (id: string): Promise<Blob> => {
    const headers = getAuthHeaders();
    const response = await fetch(`${BASE_URL}/invoice/${id}/pdf`, {
      method: "GET",
      headers: {
        ...headers,
        "Accept": "application/pdf"
      },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(errorData.message || "Failed to download PDF.");
    }
    return response.blob();
  }
};